import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { PortalTrainingItem } from './TrainingTab.tsx';

// One module card on the portal Training tab: description, pass-mark note,
// launch button, and the completion line once a pass is recorded.
export function TrainingCard({
  item,
  apiPrefix,
  listQueryKey,
}: {
  item: PortalTrainingItem;
  apiPrefix: string;
  listQueryKey: readonly unknown[];
}) {
  const qc = useQueryClient();
  const completed = item.completed_at !== null;

  const launchMutation = useMutation({
    mutationFn: () =>
      companyApi.post<{ url: string }>(
        `${apiPrefix}/training-modules/${item.module.id}/launch`,
        {},
      ),
    onSuccess: (res) => window.open(res.url, '_blank', 'noopener'),
  });

  // The run happens in another tab and records its result server-side; the
  // global query client disables refetchOnWindowFocus, so while this module is
  // incomplete refresh the list when the trainee returns to this tab.
  useEffect(() => {
    if (completed) return;
    const refresh = () => void qc.invalidateQueries({ queryKey: listQueryKey });
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [completed, qc, listQueryKey]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="text-base font-semibold text-slate-900">{item.module.title}</div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            completed ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {completed ? 'Completed' : 'Pending'}
        </span>
      </div>
      {item.module.description && (
        <p className="mt-2 text-sm leading-relaxed text-slate-700">{item.module.description}</p>
      )}
      <p className="mt-2 text-sm text-slate-600">
        This training opens in a new tab. Your quiz score is recorded automatically — you need{' '}
        <span className="font-semibold">{item.module.pass_threshold}%</span> to pass, and you can
        retake it if you don&apos;t pass.
      </p>
      {!completed && (
        <div className="mt-4">
          <Btn onClick={() => launchMutation.mutate()} loading={launchMutation.isPending}>
            Start training
          </Btn>
        </div>
      )}
      {launchMutation.error ? <ErrorMessage error={launchMutation.error} /> : null}
      {completed && (
        <div className="mt-3 text-xs text-slate-500">
          Completed on {new Date(item.completed_at!).toLocaleDateString()}
          {item.score_pct !== null ? ` · score ${item.score_pct}%` : ''}
        </div>
      )}
    </div>
  );
}
